import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { chartToneVar } from '../ensigns/chart-tone';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraText } from '../ensigns/typography';
import { SpecraChartFrame, SpecraChartLegend, SpecraChartLink } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
const defaultFormat = (v)=>v.toLocaleString();
/** A segment value; missing or negative = 0 so totals always match the drawn stack. */ const segmentValue = (c, key)=>Math.max(0, c.values[key] ?? 0);
const sumOf = (c, segments)=>segments.reduce((n, s)=>n + segmentValue(c, s.key), 0);
/** Vertical stacked columns per period, each with its total on top. */ export function SpecraStackedColumnChart({ segments, columns, format = defaultFormat, max, columnLabel = 'Period', emptyMessage, renderLink, className, getColumnTestId, getSegmentTestId, getSegmentLabel, title, description, actions, insight, 'data-testid': testId }) {
    const totals = columns.map((c)=>sumOf(c, segments));
    const empty = columns.length === 0 || totals.every((t)=>t === 0);
    const scale = max ?? Math.max(1, ...totals);
    const table = {
        columns: [
            {
                key: 'column',
                label: columnLabel
            },
            ...segments.map((s)=>({
                    key: s.key,
                    label: s.label,
                    numeric: true
                })),
            {
                key: 'total',
                label: 'Total',
                numeric: true
            }
        ],
        rows: columns.map((c, i)=>({
                id: c.id,
                cells: {
                    column: c.label,
                    ...Object.fromEntries(segments.map((s)=>[
                            s.key,
                            format(segmentValue(c, s.key))
                        ])),
                    total: c.totalLabel ?? format(totals[i])
                }
            }))
    };
    return /*#__PURE__*/ _jsx(SpecraChartFrame, {
        actions: actions,
        className: className,
        "data-testid": testId,
        description: description,
        insight: empty ? undefined : insight,
        legend: empty ? undefined : /*#__PURE__*/ _jsx(SpecraChartLegend, {
            items: segments.map((s)=>({
                    label: s.label,
                    tone: s.tone
                }))
        }),
        table: table,
        title: title,
        children: empty ? /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            "data-chart-empty": "",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "flex h-[var(--component-chart-plot-height)] items-end gap-[var(--space-3)] border-b border-[color:var(--chart-grid)]",
                    children: [
                        30,
                        55,
                        40
                    ].map((h)=>/*#__PURE__*/ _jsx("span", {
                            className: "block max-w-[var(--component-chart-column-max-width)] flex-1 rounded-t-[var(--radius-xs)] bg-[var(--chart-heat-0)]",
                            style: {
                                height: `${h}%`
                            }
                        }, h))
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "absolute inset-0 flex items-center justify-center",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "max-w-sm rounded-[var(--radius-md)] bg-[var(--surface-card)] px-[var(--space-2)] py-[var(--space-1)] text-center",
                        size: "body-ui",
                        tone: "muted",
                        children: emptyMessage
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsxs(SpecraTooltipProvider, {
            delayDuration: 100,
            children: [
                /*#__PURE__*/ _jsx("ol", {
                    "aria-label": title,
                    className: "m-0 flex h-[var(--component-chart-plot-height)] list-none items-end gap-[var(--space-3)] border-b border-[color:var(--chart-grid)] p-0",
                    children: columns.map((c, i)=>{
                        const sum = totals[i];
                        const totalLabel = c.totalLabel ?? format(sum);
                        return /*#__PURE__*/ _jsxs("li", {
                            className: "flex h-full min-w-0 max-w-[var(--component-chart-column-max-width)] flex-1 flex-col items-center justify-end",
                            "data-item-id": c.id,
                            "data-testid": getColumnTestId?.(c),
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    className: "mb-[var(--space-1)] font-medium tabular-nums",
                                    size: "meta",
                                    children: totalLabel
                                }),
                                /*#__PURE__*/ _jsx(SpecraChartLink, {
                                    "aria-label": `${c.label}: ${totalLabel}`,
                                    className: "flex w-full flex-col-reverse gap-[var(--stroke-thin)]",
                                    href: c.href,
                                    renderLink: renderLink,
                                    style: {
                                        height: `calc(${Math.min(sum, scale) / scale * 100}% - var(--space-5))`
                                    },
                                    children: segments.map((seg)=>{
                                        const v = segmentValue(c, seg.key);
                                        if (v <= 0) return null;
                                        const label = getSegmentLabel?.(seg, v);
                                        return /*#__PURE__*/ _jsx(SpecraTooltip, {
                                            content: label ?? `${seg.label}: ${format(v)}`,
                                            delayDuration: 100,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                "aria-label": label ?? `${c.label} · ${seg.label}: ${format(v)}`,
                                                className: cn('block w-full', specraFocusRing),
                                                "data-segment": seg.key,
                                                "data-testid": getSegmentTestId?.(c, seg),
                                                role: "img",
                                                style: {
                                                    flexGrow: v,
                                                    flexBasis: 0,
                                                    background: chartToneVar[seg.tone]
                                                },
                                                tabIndex: c.href ? -1 : 0
                                            })
                                        }, seg.key);
                                    })
                                })
                            ]
                        }, c.id);
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    "aria-hidden": "true",
                    className: "mt-[var(--space-1-5)] flex gap-[var(--space-3)]",
                    children: columns.map((c)=>/*#__PURE__*/ _jsx(SpecraText, {
                            className: "max-w-[var(--component-chart-column-max-width)] flex-1 text-center",
                            size: "meta",
                            tone: "muted",
                            children: c.label
                        }, c.id))
                })
            ]
        })
    });
}
